import React, { useEffect, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useBreadEvaluation } from '../context/BreadEvaluationContext';
import { PrinterIcon, Share2Icon, HistoryIcon, ClipboardCheckIcon } from 'lucide-react';
const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    evaluations
  } = useBreadEvaluation();
  const printRef = useRef<HTMLDivElement>(null);
  const currentResult = evaluations[0];
  // Don't immediately redirect if there's no result
  // Instead, we'll show a message below
  if (!currentResult) {
    return <div className="min-h-screen flex flex-col bg-gray-100" data-id="element-163">
        <Header data-id="element-164" />
        <main className="flex-grow container mx-auto px-4 py-8" data-id="element-165">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8 text-center" data-id="element-166">
            <h1 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-167">
              Nenhum resultado disponível
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-168">
              Você ainda não realizou nenhuma avaliação. Clique no botão abaixo
              para iniciar uma nova avaliação.
            </p>
            <button onClick={() => navigate('/evaluation')} className="flex items-center bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition duration-200 mx-auto" data-id="element-169">
              <ClipboardCheckIcon className="mr-2" size={20} data-id="element-170" />
              Iniciar Avaliação
            </button>
          </div>
        </main>
      </div>;
  }
  const getCategoryScores = () => {
    const categoryScores: Record<string, {
      total: number;
      max: number;
    }> = {};
    currentResult.attributes.forEach(attr => {
      if (!categoryScores[attr.category]) {
        categoryScores[attr.category] = {
          total: 0,
          max: 0
        };
      }
      categoryScores[attr.category].total += attr.score;
      categoryScores[attr.category].max += 10;
    });
    return categoryScores;
  };
  const categoryScores = getCategoryScores();
  const strengths = currentResult.attributes.filter(attr => attr.score >= 8);
  const weaknesses = currentResult.attributes.filter(attr => attr.score <= 4);
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const cssStyles = `
        body { 
          font-family: Arial, sans-serif; 
          padding: 20px;
          color: #333;
        }
        h1 { 
          color: #333; 
          border-bottom: 2px solid #ff5a1f;
          padding-bottom: 10px;
        }
        h2 { 
          color: #333; 
          margin-top: 20px;
        }
        .header { 
          background-color: #333; 
          color: white; 
          padding: 15px; 
          margin-bottom: 20px; 
        }
        .score { 
          font-size: 36px; 
          font-weight: bold; 
          color: #ff5a1f; 
          text-align: center;
        }
        .classification { 
          background-color: #f5f5f5; 
          padding: 10px; 
          border-radius: 5px; 
          margin-bottom: 20px; 
        }
        .category { 
          background-color: #f5f5f5; 
          padding: 10px; 
          border-radius: 5px; 
          margin-bottom: 10px; 
        }
        .category-header { 
          display: flex; 
          justify-content: space-between; 
          margin-bottom: 5px; 
        }
        .progress-bar { 
          height: 10px; 
          background-color: #ddd; 
          border-radius: 5px; 
        }
        .progress-fill { 
          height: 10px; 
          background-color: #ff5a1f; 
          border-radius: 5px; 
        }
        .grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 20px; 
          margin-top: 20px; 
        }
        .point-item { 
          display: flex; 
          align-items: center; 
          margin-bottom: 5px; 
        }
        .point-score { 
          display: inline-block; 
          width: 24px; 
          height: 24px; 
          border-radius: 50%; 
          text-align: center; 
          line-height: 24px; 
          margin-right: 10px; 
          font-size: 12px; 
        }
        .strong { 
          background-color: #e6f4ea; 
          color: #137333; 
        }
        .weak { 
          background-color: #fce8e6; 
          color: #c5221f; 
        }
        .footer { 
          margin-top: 30px; 
          text-align: center; 
          font-size: 12px; 
          color: #666; 
        }
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { 
            display: none; 
          }
        }
      `;
      const categoriesHTML = Object.entries(categoryScores).map(([category, {
        total,
        max
      }]) => `
          <div class="category">
            <div class="category-header">
              <span style="font-weight: bold;">${category}</span>
              <span>${total} / ${max} pontos</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${total / max * 100}%;"></div>
            </div>
          </div>
        `).join('');
      const strengthsHTML = strengths.length > 0 ? `<ul style="list-style-type: none; padding: 0;">
              ${strengths.map(attr => `
                <li class="point-item">
                  <span class="point-score strong">${attr.score}</span>
                  <span>${attr.name}</span>
                </li>
              `).join('')}
            </ul>` : `<p style="font-style: italic; color: #666;">Nenhum ponto forte identificado.</p>`;
      const weaknessesHTML = weaknesses.length > 0 ? `<ul style="list-style-type: none; padding: 0;">
              ${weaknesses.map(attr => `
                <li class="point-item">
                  <span class="point-score weak">${attr.score}</span>
                  <span>${attr.name}</span>
                </li>
              `).join('')}
            </ul>` : `<p style="font-style: italic; color: #666;">Nenhum ponto crítico identificado.</p>`;
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Avaliação de Pão Francês - ${currentResult.sampleNumber}</title>
            <style>{`;
      $;
      {
        cssStyles;
      }
      ;
      `}</style>
          </head>
          <body>
            <div class="header">
              <h1>Resultado da Avaliação de Pão Francês</h1>
              <p>Amostra: ${currentResult.sampleNumber} | Data: ${currentResult.date} às ${currentResult.time}</p>
            </div>
            <div style="display: flex; margin-bottom: 20px;">
              <div style="flex: 1; text-align: center;">
                <h2>Pontuação Total</h2>
                <div class="score">${currentResult.totalScore}</div>
                <p>de 130 pontos possíveis</p>
              </div>
              <div style="flex: 1; padding-left: 20px; border-left: 1px solid #ddd;">
                <h2>Classificação</h2>
                <div class="classification">
                  <p style="font-weight: bold;">${currentResult.classification}</p>
                  <div class="progress-bar">
                    <div class="progress-fill" style="width: ${Math.min(currentResult.totalScore / 130 * 100, 100)}%;"></div>
                  </div>
                </div>
              </div>
            </div>
            <h2>Pontuação por Categoria</h2>
            <div style="margin-bottom: 20px;">
              ${categoriesHTML}
            </div>
            <div class="grid">
              <div>
                <h2>Pontos Fortes</h2>
                ${strengthsHTML}
              </div>
              <div>
                <h2>Pontos Críticos</h2>
                ${weaknessesHTML}
              </div>
            </div>
            <div class="footer">
              <p>Baseado na norma ABNT NBR 16170</p>
              <p>Relatório gerado em ${new Date().toLocaleDateString()} às ${new Date().toLocaleTimeString()}</p>
            </div>
          </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };
  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(`
*Resultado da Avaliação de Pão Francês*
*Amostra:* ${currentResult.sampleNumber}
*Data:* ${currentResult.date} às ${currentResult.time}
*Pontuação Total:* ${currentResult.totalScore}/130
*Classificação:* ${currentResult.classification}
Para mais detalhes, solicite o relatório completo em PDF.
    `);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };
  return <div className="min-h-screen flex flex-col bg-gray-100" data-id="element-171">
      <Header data-id="element-172" />
      <main className="flex-grow container mx-auto px-4 py-8" data-id="element-173">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-174">
          <div className="bg-gray-800 text-white p-6" data-id="element-175">
            <h1 className="text-2xl font-bold mb-2" data-id="element-176">Resultado da Avaliação</h1>
            <p data-id="element-177">
              Amostra: {currentResult.sampleNumber} | Data: {currentResult.date}{' '}
              às {currentResult.time}
            </p>
          </div>
          <div className="p-8" ref={printRef} data-id="element-178">
            <div className="flex flex-col md:flex-row md:items-center mb-8" data-id="element-179">
              <div className="md:w-1/2 mb-6 md:mb-0" data-id="element-180">
                <div className="text-center" data-id="element-181">
                  <h2 className="text-xl font-bold text-gray-800 mb-2" data-id="element-182">
                    Pontuação Total
                  </h2>
                  <div className="text-5xl font-bold text-orange-500 mb-2" data-id="element-183">
                    {currentResult.totalScore}
                  </div>
                  <p className="text-gray-600" data-id="element-184">de 130 pontos possíveis</p>
                </div>
              </div>
              <div className="md:w-1/2 md:pl-8 md:border-l border-gray-200" data-id="element-185">
                <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-186">
                  Classificação
                </h2>
                <div className="bg-gray-100 p-4 rounded-lg" data-id="element-187">
                  <p className="text-lg font-semibold mb-1" data-id="element-188">
                    {currentResult.classification}
                  </p>
                  <div className="w-full bg-gray-300 rounded-full h-2.5" data-id="element-189">
                    <div className={`h-2.5 rounded-full ${currentResult.totalScore >= 130 ? 'bg-green-500' : currentResult.totalScore >= 100 ? 'bg-blue-500' : currentResult.totalScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                    width: `${Math.min(currentResult.totalScore / 130 * 100, 100)}%`
                  }} data-id="element-190"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-8" data-id="element-191">
              <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-192">
                Pontuação por Categoria
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-193">
                {Object.entries(categoryScores).map(([category, {
                total,
                max
              }]) => <div key={category} className="bg-gray-50 p-4 rounded-lg" data-id="element-194">
                      <div className="flex justify-between items-center mb-2" data-id="element-195">
                        <h3 className="font-medium" data-id="element-196">{category}</h3>
                        <span className="text-sm text-gray-600" data-id="element-197">
                          {total} / {max} pontos
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-198">
                        <div className="bg-orange-500 h-2.5 rounded-full" style={{
                    width: `${total / max * 100}%`
                  }} data-id="element-199"></div>
                      </div>
                    </div>)}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-200">
              <div data-id="element-201">
                <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-202">
                  Pontos Fortes
                </h2>
                {strengths.length > 0 ? <ul className="space-y-2" data-id="element-203">
                    {strengths.map(attr => <li key={attr.id} className="flex items-center" data-id="element-204">
                        <span className="inline-block w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs flex items-center justify-center mr-2" data-id="element-205">
                          {attr.score}
                        </span>
                        <span data-id="element-206">{attr.name}</span>
                      </li>)}
                  </ul> : <p className="text-gray-500 italic" data-id="element-207">
                    Nenhum ponto forte identificado.
                  </p>}
              </div>
              <div data-id="element-208">
                <h2 className="text-xl font-bold text-gray-800 mb-4" data-id="element-209">
                  Pontos Críticos
                </h2>
                {weaknesses.length > 0 ? <ul className="space-y-2" data-id="element-210">
                    {weaknesses.map(attr => <li key={attr.id} className="flex items-center" data-id="element-211">
                        <span className="inline-block w-6 h-6 rounded-full bg-red-100 text-red-600 text-xs flex items-center justify-center mr-2" data-id="element-212">
                          {attr.score}
                        </span>
                        <span data-id="element-213">{attr.name}</span>
                      </li>)}
                  </ul> : <p className="text-gray-500 italic" data-id="element-214">
                    Nenhum ponto crítico identificado.
                  </p>}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-6 p-8 flex flex-wrap gap-3" data-id="element-215">
            <button onClick={handlePrint} className="flex items-center bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded-md transition duration-200" data-id="element-216">
              <PrinterIcon size={18} className="mr-2" data-id="element-217" />
              Imprimir
            </button>
            <button onClick={handleWhatsAppShare} className="flex items-center bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md transition duration-200" data-id="element-218">
              <Share2Icon size={18} className="mr-2" data-id="element-219" />
              Compartilhar via WhatsApp
            </button>
            <button onClick={() => navigate('/history')} className="flex items-center bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-md transition duration-200 ml-auto" data-id="element-220">
              <HistoryIcon size={18} className="mr-2" data-id="element-221" />
              Ver Histórico
            </button>
          </div>
        </div>
      </main>
    </div>;
};
export default ResultsPage;